import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage, guestApi } from "@/lib/api";

const GUEST_KEY = "hubpilot-guest-project";
const OLD_GUEST_KEY = "hubpilot-guest-program";
const MIGRATED_KEY = "hubpilot-guest-migrated";

export const readGuestProject = () => {
  try {
    const p = JSON.parse(localStorage.getItem(GUEST_KEY));
    return p?.config && typeof p.name === "string" && p.name.trim() ? p : null;
  } catch {
    return null;
  }
};

export const clearGuestProject = () => localStorage.removeItem(GUEST_KEY);

// Keeps a robot setup saved in this browser before HubPilot became setup-only; other old programs stay hidden.
export async function migrateOldGuest() {
  const raw = localStorage.getItem(OLD_GUEST_KEY);
  if (!raw || localStorage.getItem(MIGRATED_KEY) || readGuestProject()) return;
  try {
    const old = JSON.parse(raw);
    const { config } = old?.workspace ? await guestApi.post("/legacy/convert", { workspace: old.workspace }) : { config: null };
    if (config) localStorage.setItem(GUEST_KEY, JSON.stringify({ name: old.name?.trim() || "My robot", config, updated_at: old.updated_at }));
    localStorage.setItem(MIGRATED_KEY, "1");
  } catch {
    // Offline or a broken old save: try again next visit.
  }
}

export function useCloudProject(id, api) {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [name, setName] = useState("");
  const [saveState, setSaveState] = useState("saved");

  useEffect(() => {
    api.get(`/programs/${id}`)
      .then((p) => { setProject(p); setName(p.name); })
      .catch(() => { toast.error("That project could not be found"); navigate("/projects", { replace: true }); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setSaveState("saving");
    try {
      setProject(await api.put(`/programs/${id}`, patch));
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      throw e;
    }
  };

  const commitName = async (value = name) => {
    const trimmed = value.trim();
    if (!trimmed) return setName(project.name);
    if (trimmed === project.name) return;
    try {
      await save({ name: trimmed });
      toast.success("Project renamed");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const saveSetup = async (config, newName) => {
    await save({ config, ...(newName && { name: newName }) });
    if (newName) setName(newName);
  };

  return { project, name, setName, commitName, saveState, saveSetup };
}

export function useGuestProject() {
  const [project, setProject] = useState(null);
  const [name, setName] = useState("");
  const latest = useRef(null);

  useEffect(() => {
    migrateOldGuest().finally(() => {
      latest.current = readGuestProject() || { name: "", config: null };
      setProject(latest.current);
      setName(latest.current.name);
    });
  }, []);

  const persist = useCallback((patch) => {
    latest.current = { ...latest.current, ...patch, updated_at: new Date().toISOString() };
    localStorage.setItem(GUEST_KEY, JSON.stringify(latest.current));
    setProject(latest.current);
  }, []);

  const commitName = (value = name) => {
    const trimmed = value.trim();
    if (!trimmed || !latest.current?.config) return setName(latest.current?.name || "");
    if (trimmed !== latest.current.name) persist({ name: trimmed });
  };

  const saveSetup = async (config, newName) => {
    persist({ config, ...(newName && { name: newName }) });
    if (newName) setName(newName);
  };

  return { project, name, setName, commitName, saveState: "saved", saveSetup };
}
